import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Search as SearchIcon, ArrowLeft, Users, Building2, Link2, 
  FileText, CheckSquare, ChevronRight 
} from 'lucide-react';

export default function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, loading: authLoading } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Get the referrer from state or default to /app
  const referrer = location.state?.from || '/app';

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/login');
    }
  }, [authLoading, token, navigate]);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    
    setLoading(true);
    setSearched(true);
    try {
      const response = await api.search(token, query);
      setResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
    }
    setLoading(false);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'Client': return <Users className="w-4 h-4" />;
      case 'Individual': return <Users className="w-4 h-4" />;
      case 'Entity': return <Building2 className="w-4 h-4" />;
      case 'Link': return <Link2 className="w-4 h-4" />;
      case 'Task': return <CheckSquare className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const handleResultClick = (result) => {
    switch (result.type) {
      case 'Client':
        navigate(`/app/clients/${result.id}`);
        break;
      case 'Individual':
        navigate(`/app/clients/${result.clientId}/individuals/${result.id}`);
        break;
      case 'Entity':
        navigate(`/app/clients/${result.clientId}/entities/${result.id}`);
        break;
      case 'Link':
        navigate(`/app/clients/${result.clientId}/links`);
        break;
      case 'Task':
        navigate(`/app/clients/${result.clientId}/workflow`);
        break;
      default:
        break;
    }
  };

  // Saved searches
  const savedSearches = [
    { label: 'Upcoming obligations', query: 'due' },
    { label: 'Restricted households', query: 'restricted' },
    { label: 'KYC due', query: 'kyc' },
    { label: 'Trust deeds', query: 'deed' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F5]" data-testid="search-page">
      {/* Header */}
      <header className="bg-white border-b border-[#D9D6D0] px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(referrer)}
            className="caffeine-btn-ghost"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-8 py-12">
        {/* Search Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl text-[#2C2420] mb-2">Enterprise Search</h1>
          <p className="text-[#5D524D]">
            Search across clients, people, entities, tasks, and documents
          </p>
        </div>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8C8078]" />
            <Input
              placeholder="Search for clients, people, entities, documents..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="caffeine-input pl-12 pr-24 h-14 text-lg"
              data-testid="search-input"
            />
            <Button 
              type="submit"
              className="caffeine-btn-primary absolute right-2 top-1/2 -translate-y-1/2"
              disabled={loading}
              data-testid="search-submit-btn"
            >
              Search
            </Button>
          </div>
        </form>

        {/* Saved Searches */}
        <div className="mb-8">
          <p className="text-sm text-[#8C8078] mb-3">Quick searches:</p>
          <div className="flex flex-wrap gap-2">
            {savedSearches.map((saved) => (
              <button
                key={saved.query}
                onClick={() => { setQuery(saved.query); handleSearch(); }}
                className="px-3 py-1.5 bg-[#F0EBE6] hover:bg-[#E6E0D9] border border-[#D9D6D0] rounded-full text-sm text-[#5D524D] transition-colors"
                data-testid={`saved-search-${saved.query}`}
              >
                {saved.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-[#5D524D]">Searching...</p>
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="empty-state">
            <SearchIcon className="w-12 h-12" />
            <p className="text-lg font-medium text-[#5D524D]">No results found</p>
            <p className="text-sm">Try different keywords or check spelling</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-[#8C8078] mb-4">
              {results.length} result{results.length !== 1 ? 's' : ''} found
            </p>
            {results.map((result) => (
              <div
                key={`${result.type}-${result.id}`}
                onClick={() => handleResultClick(result)}
                className="caffeine-card p-4 cursor-pointer flex items-center justify-between"
                data-testid={`search-result-${result.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#4A3B32]/10 rounded-lg flex items-center justify-center text-[#4A3B32]">
                    {getIcon(result.type)}
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2420]">
                      {result.name || result.title}
                    </p>
                    <div className="flex items-center gap-2 text-sm text-[#8C8078]">
                      <span className="px-2 py-0.5 bg-[#F0EBE6] rounded text-xs">
                        {result.type}
                      </span>
                      {result.segment && <span>{result.segment}</span>}
                      {result.role && <span>{result.role}</span>}
                      {result.entityType && <span>{result.entityType}</span>}
                      {result.sourceSystem && <span>{result.sourceSystem}</span>}
                      {result.dueDate && <span>Due: {result.dueDate}</span>}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#8C8078]" />
              </div>
            ))}
          </div>
        )}

        {/* Hint */}
        {!searched && (
          <div className="text-center py-8 text-sm text-[#8C8078]">
            <p>Try searching for "Baker", "Trust Deed", "Williams", or "KYC"</p>
            <p className="mt-1">Fuzzy search: searching "deed" will also find "Trust Deed"</p>
          </div>
        )}
      </main>
    </div>
  );
}
